import { defineStore } from 'pinia';
import { ProvidersFeesListItemResponse } from '@/api/response/providers-fees/providers-fees-list-item.response';
import { ApiError, client } from '@/api';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ProvidersFeesGetListRequest } from '@/api/request/providers-fees/providers-fees-get-list.request';
import {
  CreateProviderFeeRequest,
  PaginatedProviderFeeResponse,
  ProviderFeeDictAggregatorResponse,
  ProviderFeeDictProviderResponse,
  ProviderFeeResponse,
  UpdateProviderFeeRequest,
} from '@/open-api/models';
import { debounce } from 'es-toolkit';
import { watch } from 'vue';

export type ProvidersFeesState = {
  isWatching: boolean;
  loading: boolean;
  error: string | null;
  currentProviderFee: ProvidersFeesListItemResponse | null;
  isCreateOpened: boolean;
  isUpdateOpened: boolean;
  isDeleteOpened: boolean;

  search: string | null;
  aggregatorId: number | string | null;
  providerId: number | string | null;
  fee: number | string | null;

  fees: ProviderFeeResponse[];

  pagination: {
    page: number;
    perPage: number;
    total: number;
    pageCount: number;
  };
};

export type CascaderProviderFeeChild = {
  label: string;
  value: string;
  providerFeeId: string;
  fee: number;
};

export type CascaderProviderFee = {
  label: string;
  value: string;
  children: CascaderProviderFeeChild[];
};

const ALL: CascaderProviderFee[] = [{ label: 'All', value: '', children: [] }];

const DEFAULT_PAGINATION = {
  page: 1,
  perPage: 15,
  total: 0,
  pageCount: 0,
};

export const useProvidersFees = defineStore('providersFees', {
  state: (): ProvidersFeesState => ({
    isWatching: false,
    loading: false,
    error: null,
    currentProviderFee: null,
    isCreateOpened: false,
    isUpdateOpened: false,
    isDeleteOpened: false,

    search: null,
    aggregatorId: null,
    providerId: null,
    fee: null,

    fees: [],

    pagination: { ...DEFAULT_PAGINATION },
  }),

  actions: {
    init() {
      const debounceFetch = debounce(() => {
        if (this.isWatching) {
          this.refresh();
        }
      }, 300);

      watch(
        () => [this.search, this.aggregatorId, this.providerId, this.fee],
        () => debounceFetch(),
        { immediate: true },
      );
    },

    reset() {
      this.$reset();
    },

    async refresh() {
      await this.listProvidersFees({});
    },

    setLoading(value: boolean) {
      this.loading = value;
    },
    setError(value: string | null) {
      this.error = value;
    },
    setCurrentFee(item: ProvidersFeesListItemResponse | null) {
      this.currentProviderFee = item;
    },

    openCreate() {
      this.isCreateOpened = true;
    },

    openUpdate(item: ProvidersFeesListItemResponse) {
      this.setCurrentFee(item);
      this.isUpdateOpened = true;
    },

    openDelete(item: ProvidersFeesListItemResponse) {
      this.setCurrentFee(item);
      this.isDeleteOpened = true;
    },

    closeCreate() {
      this.isCreateOpened = false;
    },
    closeUpdate() {
      this.isUpdateOpened = false;
    },
    closeDelete() {
      this.isDeleteOpened = false;
    },

    setPagination({ page, limit }: { page: number; limit: number }): void {
      this.pagination = {
        ...this.pagination,
        page,
        perPage: limit,
      };

      this.refresh();
    },

    async listProvidersFees(
      dto: ProvidersFeesGetListRequest = {},
    ): Promise<PaginatedProviderFeeResponse> {
      this.setError(null);
      this.setLoading(true);

      let data: PaginatedProviderFeeResponse = {
        items: [],
        total: 0,
        page: 1,
        perPage: 10,
        pageCount: 1,
      };

      try {
        data = await client.providersFeesApi.listProvidersFees({
          ...dto,
          fee: (this.fee as number) || undefined,
          aggregatorId: (this.aggregatorId as number) || undefined,
          providerId: (this.providerId as number) || undefined,

          message:
            this.search && this.search.length >= 3 ? this.search : undefined,

          pageNumber: this.pagination.page,
          pageSize: this.pagination.perPage,
        });

        const { items, ...pagination } = data;

        this.fees = items ?? [];
        this.pagination = pagination;
      } catch (err) {
        let message = 'Error during getting list of Providers Fees';

        if ((err as ApiError)?.body?.message) {
          message = (err as ApiError).body.message;
        }

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return data;
    },

    async getProvidersFeesDist(): Promise<CascaderProviderFee[]> {
      this.setError(null);
      this.setLoading(true);

      try {
        const data: ProviderFeeDictAggregatorResponse[] =
          await client.providersFeesApi.getProvidersFeesDist();

        return [...ALL].concat(
          data.map(
            (agg: ProviderFeeDictAggregatorResponse): CascaderProviderFee => ({
              label: agg.title,
              value: agg.id.toString(),
              children: (agg.providers || []).map(
                (
                  prov: ProviderFeeDictProviderResponse,
                ): CascaderProviderFeeChild => ({
                  label: `${prov.title} (fee: ${prov.fee}%)`,
                  value: prov.id.toString(),
                  providerFeeId: prov.providerFeeId.toString(),
                  fee: prov.fee,
                }),
              ),
            }),
          ),
        );
      } catch (err) {
        let message =
          'Error during getting list of available Aggregators and Providers Fees';

        if ((err as ApiError)?.body?.message) {
          message = (err as ApiError).body.message;
        }

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return [];
    },

    async createProviderFeeItem(
      dto: CreateProviderFeeRequest,
    ): Promise<ProviderFeeResponse | null> {
      this.setError(null);
      this.setLoading(true);

      try {
        return await client.providersFeesApi.createProviderFeeItem(dto);
      } catch (err) {
        const message =
          'Error during creating Provider Fee. Check if the same Fee is already existed.';

        ElMessage.error(message);
      } finally {
        this.setLoading(false);
      }

      return null;
    },

    async updateProviderFeeItem(
      id: number,
      dto: UpdateProviderFeeRequest,
    ): Promise<ProviderFeeResponse | null> {
      this.setError(null);
      this.setLoading(true);

      try {
        return await client.providersFeesApi.updateProviderFeeItem(id, dto);
      } catch (err) {
        const message = 'Error during updating Provider Fee';

        ElMessage.error(message);
      } finally {
        this.setLoading(false);
      }

      return null;
    },

    async deleteProviderFeeItem(id: number): Promise<void> {
      try {
        await ElMessageBox.confirm('Please confirm deleting Provider Fee');
      } catch (err) {
        return;
      }

      this.setError(null);
      this.setLoading(true);

      try {
        await client.providersFeesApi.deleteProviderFeeItem(id);
      } catch (err) {
        const message = 'Error during removing Provider Fee';

        ElMessage.error(message);
      } finally {
        this.setLoading(false);
      }
    },
  },
});
